import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKeyhole, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/config/brand";
import { TESTIDS } from "@/constants/testIds";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.login(email.trim().toLowerCase(), password);
      localStorage.setItem("vs_admin_token", res.token);
      localStorage.setItem("vs_admin_email", res.email);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--vs-bg)] flex items-center justify-center px-5 py-16 relative">
      <div className="absolute inset-0 blueprint-grid-lg opacity-60 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10">
          <img src={BRAND.logo} alt="Vasquez & Son" className="h-14 w-14 object-contain" />
          <div className="text-white font-display uppercase tracking-tight text-lg">
            Vasquez & Son
          </div>
        </Link>

        <div className="border border-white/10 bg-[#0f0f10] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2 text-[color:var(--vs-blue)]">
            <LockKeyhole className="w-4 h-4" />
            <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase">
              Admin console
            </span>
          </div>
          <h1 className="font-display uppercase text-3xl text-white font-bold tracking-tight leading-tight">
            Sign in
          </h1>
          <p className="text-sm text-gray-400 mt-2">
            Manage incoming leads and quote requests.
          </p>

          <form onSubmit={submit} data-testid={TESTIDS.admin.loginForm} className="mt-8 flex flex-col gap-4">
            <div>
              <label className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2 block">
                Email
              </label>
              <input
                type="email"
                required
                data-testid={TESTIDS.admin.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-[#27272a] text-white px-4 py-3 focus:border-[color:var(--vs-blue)] focus:ring-1 focus:ring-[color:var(--vs-blue)] outline-none"
                placeholder="admin@vasquezson.com"
              />
            </div>
            <div>
              <label className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2 block">
                Password
              </label>
              <input
                type="password"
                required
                data-testid={TESTIDS.admin.loginPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black border border-[#27272a] text-white px-4 py-3 focus:border-[color:var(--vs-blue)] focus:ring-1 focus:ring-[color:var(--vs-blue)] outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid={TESTIDS.admin.loginSubmit}
              className="btn-vs btn-primary mt-2 w-full justify-center disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        <Link
          to="/"
          className="mt-6 block text-center text-xs font-mono-accent tracking-[0.25em] uppercase text-white/40 hover:text-white transition-colors"
        >
          ← Back to site
        </Link>
      </div>
    </div>
  );
}
